import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSchemas } from "@/contexts/SchemaContext";
import { Badge } from "@/components/ui/badge";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { toast } from "sonner";
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const STATUS = {
  on_target: ["On target", "bg-emerald-100 text-emerald-800 border-emerald-200"],
  near_target: ["Close to target", "bg-amber-100 text-amber-800 border-amber-200"],
  below_target: ["Below target", "bg-red-100 text-red-800 border-red-200"],
};
const H = "text-xs uppercase tracking-[0.18em] text-purple-700 mb-3";

// "How is this patient actually doing against what was set?" Built from what the headset reports each round.
export default function RecoveryPanel({ patientId }) {
  const { patient: pdata } = useSchemas();
  const [days, setDays] = useState(14);
  const [rec, setRec] = useState(null);

  useEffect(() => {
    setRec(null);
    api.get(`/patients/${patientId}/recovery`, { params: { days } }).then((r) => setRec(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [patientId, days]);

  if (!rec) return <div className="text-sm text-slate-400">Loading…</div>;
  const empty = rec.dose.rounds === 0 && rec.domains.length === 0;
  const cells = pdata?.reachZones?.cells || [];
  const maxZone = Math.max(1, ...Object.values(rec.zone_counts || {}));
  const dailyMax = Math.max(rec.dose.target_minutes || 0, ...rec.dose.daily.map((d) => d.minutes), 1);

  return (
    <div className="space-y-5" data-testid="recovery">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-slate-600 max-w-2xl">
          How this patient is doing against what was set: time spent, hits and misses, reach against their goal, and how each measure has moved compared with the {days} days before.
          Everything comes from the rounds reported by the headset.
        </p>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-9 rounded-md border border-input bg-transparent text-sm px-2" data-testid="recovery-days">
          {[7, 14, 30, 90].map((d) => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </div>

      {empty && <div className="panel rounded-2xl p-10 text-center text-sm text-slate-500" data-testid="recovery-empty">No rounds were reported in this period yet.</div>}

      {rec.insights.length > 0 && (
        <div className="panel rounded-2xl p-5" data-testid="recovery-insights">
          <div className={H}>What stands out</div>
          <ul className="space-y-1.5 text-sm text-slate-800 list-disc pl-5">{rec.insights.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}

      {!empty && <Charts rec={rec} />}

      {!empty && (
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="panel rounded-2xl p-5" data-testid="recovery-dose">
            <div className={H}>Time in therapy</div>
            <div className="flex flex-wrap gap-6 mb-3">
              <Stat label="Days target met" value={rec.dose.target_minutes ? `${rec.dose.days_met_target} of ${rec.dose.window_days}` : "no target set"} />
              <Stat label="Active days" value={rec.dose.active_days} />
              <Stat label="Rounds" value={rec.dose.rounds} sub={`was ${rec.dose.previous_rounds}`} />
              <Stat label="Minutes per active day" value={rec.dose.minutes_per_active_day ?? "-"} />
            </div>
            <div className="flex items-end gap-1 h-24 border-b border-slate-200 relative">
              {rec.dose.target_minutes ? <div className="absolute left-0 right-0 border-t border-dashed border-purple-400" style={{ bottom: `${(rec.dose.target_minutes / dailyMax) * 100}%` }} title={`Daily target ${rec.dose.target_minutes} min`} /> : null}
              {rec.dose.daily.map((d) => (
                <div key={d.day} title={`${d.day}: ${d.minutes} min`} className={`flex-1 rounded-t ${rec.dose.target_minutes && d.minutes >= rec.dose.target_minutes ? "bg-emerald-500" : "bg-purple-400"}`}
                  style={{ height: `${(d.minutes / dailyMax) * 100}%` }} />
              ))}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">Each bar is a day of therapy. Green = the daily target was met; the dashed line is the target.</div>
          </div>

          <div className="panel rounded-2xl p-5" data-testid="recovery-reach">
            <div className={H}>Reach against goal</div>
            {rec.reach.length === 0 && <div className="text-sm text-slate-500">No reach measurements yet.</div>}
            {rec.reach.map((r) => (
              <div key={r.side} className="mb-3">
                <div className="flex justify-between text-sm"><span className="font-medium">{r.side} arm</span>
                  <span className="text-slate-600">{r.reached_m ?? "-"} m of {r.goal_m ?? "-"} m{r.pct_of_goal != null ? ` · ${r.pct_of_goal}%` : ""}</span></div>
                <div className="h-2.5 rounded bg-slate-100 overflow-hidden"><div className="h-full bg-purple-500" style={{ width: `${Math.min(100, r.pct_of_goal || 0)}%` }} /></div>
              </div>
            ))}
            {cells.length === 9 && (
              <div className="mt-4">
                <div className="text-xs text-slate-500 mb-1">Targets reached by area (darker = more)</div>
                <div className="inline-grid grid-cols-3 gap-1" data-testid="zone-counts">
                  {cells.map((c) => {
                    const n = rec.zone_counts?.[c] || 0;
                    return <div key={c} title={`${c}: ${n}`} className="h-9 w-14 rounded text-[10px] grid place-items-center border border-slate-200"
                      style={{ background: n ? `rgba(147,51,234,${0.15 + 0.7 * (n / maxZone)})` : "#f8fafc", color: n / maxZone > 0.5 ? "white" : "#475569" }}>{n}</div>;
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {rec.games.length > 0 && (
        <div className="panel rounded-2xl overflow-x-auto" data-testid="recovery-games">
          <div className="px-5 pt-5"><div className={H}>Hits and misses by activity</div></div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 text-left"><tr>
              {["Activity", "Level", "Rounds", "Hits", "Misses", "Success", "Target", "Status", "Level changes"].map((h) => <th key={h} className="px-4 py-2 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {rec.games.map((g) => (
                <tr key={g.game} className="border-t border-slate-100" data-testid={`recovery-game-${g.game}`}>
                  <td className="px-4 py-2 font-medium">{g.name}</td>
                  <td className="px-4 py-2">{g.level ?? "-"}</td>
                  <td className="px-4 py-2">{g.rounds}</td>
                  <td className="px-4 py-2 text-emerald-700">{g.successes}</td>
                  <td className="px-4 py-2 text-red-700">{g.misses}</td>
                  <td className="px-4 py-2">{g.success_pct != null ? `${g.success_pct}%` : "-"}</td>
                  <td className="px-4 py-2">{g.target_pct != null ? `${g.target_pct}%` : "-"}</td>
                  <td className="px-4 py-2">{g.status ? <Badge variant="outline" className={`text-[10px] ${STATUS[g.status][1]}`}>{STATUS[g.status][0]}</Badge> : "-"}</td>
                  <td className="px-4 py-2">{g.level_changes}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 py-2 text-[11px] text-slate-500">Target = the "success rate to aim for" set for this patient's treatment area. A level change by the headset is counted in the last column.</div>
        </div>
      )}

      {rec.domains.map((d) => (
        <div key={d.name} className="panel rounded-2xl p-5" data-testid={`recovery-domain-${d.name}`}>
          <div className={H}>{d.name}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {d.metrics.map((m) => (
              <div key={m.key} className="rounded-lg border border-slate-200 p-3" data-testid={`metric-${m.key}`}>
                <div className="text-xs text-slate-500">{m.label}</div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-xl font-semibold text-slate-900 tabular-nums">{m.value ?? "-"}</span>
                  <span className="text-xs text-slate-400">{m.unit}</span>
                  {m.delta != null && (
                    <span className={`ml-auto inline-flex items-center text-xs ${m.direction === "better" ? "text-emerald-700" : m.direction === "worse" ? "text-red-700" : "text-slate-500"}`}
                      title={`Before: ${m.previous ?? "-"}`}>
                      {m.delta > 0 ? <ArrowUp className="h-3 w-3" /> : m.delta < 0 ? <ArrowDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                      {Math.abs(m.delta)}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-400">{m.agg === "sum" ? "total" : m.agg === "max" ? "best" : m.agg === "last" ? "latest" : "average"} · before: {m.previous ?? "-"}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const COL = { Left: "hsl(190 80% 42%)", Right: "hsl(300 70% 48%)" };
const LINES = ["hsl(271 70% 55%)", "hsl(198 90% 45%)", "hsl(160 60% 40%)", "hsl(35 90% 50%)", "hsl(0 70% 55%)"];
const short = (d) => d.slice(5);

function ChartCard({ title, question, children, caption, testid }) {
  return (
    <div className="panel rounded-2xl p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.18em] text-purple-700">{title}</div>
      <div className="text-sm text-slate-700 mt-0.5 mb-2">{question}</div>
      <div className="h-56">{children}</div>
      {caption && <div className="text-[11px] text-slate-500 mt-2">{caption}</div>}
    </div>
  );
}

// The three questions therapists ask: reaching farther? gripping better? hitting the targets?
function Charts({ rec }) {
  const s = rec.series;
  const goals = Object.fromEntries(rec.reach.map((r) => [r.side, r.goal_m]));
  const beyond = rec.reach.filter((r) => r.beyond_goal_days > 0);
  const reachDays = [...new Set([...s.reach.Left, ...s.reach.Right].map((p) => p.day))].sort();
  const reachData = reachDays.map((day) => ({ day: short(day), Left: s.reach.Left.find((p) => p.day === day)?.value, Right: s.reach.Right.find((p) => p.day === day)?.value }));
  const dot = (side) => (p) => (p.cx == null || p.payload[side] == null ? null
    : <circle key={`${side}-${p.index}`} cx={p.cx} cy={p.cy} r={goals[side] && p.payload[side] > goals[side] ? 6 : 3.5}
        fill={goals[side] && p.payload[side] > goals[side] ? "hsl(35 95% 50%)" : COL[side]} stroke="white" strokeWidth={1} />);
  const gripData = s.grip.points.map((p) => ({ day: short(p.day), grip: p.value }));
  const games = Object.keys(s.success);
  const sDays = [...new Set(games.flatMap((g) => s.success[g].map((p) => p.day)))].sort();
  const sData = sDays.map((day) => ({ day: short(day), ...Object.fromEntries(games.map((g) => [g, s.success[g].find((p) => p.day === day)?.pct])) }));
  const name = (id) => rec.games.find((g) => g.game === id)?.name || id;
  const target = rec.games.find((g) => g.target_pct != null)?.target_pct;

  return (
    <div className="grid lg:grid-cols-3 gap-5" data-testid="recovery-charts">
      <ChartCard testid="chart-reach" title="Reaching" question="Is the patient reaching farther?"
        caption={beyond.length
          ? `Dashed line = the reach goal. Amber dots = reached beyond the goal (${beyond.map((r) => `${r.side}: ${r.beyond_goal_days} day${r.beyond_goal_days > 1 ? "s" : ""}`).join(", ")}). The goal may be set too low: check the arm reach on the Upper Body tab.`
          : "Dashed lines = each arm's configured reach goal. Higher is farther."}>
        {reachData.length === 0 ? <Empty text="No reach measurements yet." /> : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={reachData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} width={34} unit=" m" domain={[0, "auto"]} />
              <Tooltip />
              {goals.Left ? <ReferenceLine y={goals.Left} stroke={COL.Left} strokeDasharray="5 4" /> : null}
              {goals.Right ? <ReferenceLine y={goals.Right} stroke={COL.Right} strokeDasharray="5 4" /> : null}
              <Line type="monotone" dataKey="Left" stroke={COL.Left} strokeWidth={2} dot={dot("Left")} connectNulls />
              <Line type="monotone" dataKey="Right" stroke={COL.Right} strokeWidth={2} dot={dot("Right")} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard testid="chart-grip" title="Gripping" question="Is their grip improving?"
        caption={s.grip.required.length ? `Dashed line = the grip closure the level asks for (${s.grip.required.map((q) => `${q.name} L${q.level}: ${q.pct}%`).join(", ")}).` : "Higher is a firmer grip closure."}>
        {gripData.length === 0 ? <Empty text="Grip is not being reported by the headset yet. It will appear here as soon as it is." /> : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={gripData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} width={34} unit="%" domain={[0, 100]} />
              <Tooltip />
              {s.grip.required.map((q, i) => <ReferenceLine key={q.game} y={q.pct} stroke={LINES[(i + 3) % LINES.length]} strokeDasharray="5 4" />)}
              <Line type="monotone" dataKey="grip" stroke={LINES[0]} strokeWidth={2} dot={{ r: 3.5 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard testid="chart-success" title="Hitting the targets" question="Are they succeeding at what is asked?"
        caption={target != null ? `Dashed line = the success rate set as the aim (${target}%). Each line is one activity.` : "Each line is one activity: the share of attempts that succeeded each day."}>
        {sData.length === 0 ? <Empty text="No attempts reported yet." /> : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} width={34} unit="%" domain={[0, 100]} />
              <Tooltip formatter={(v, k) => [`${v}%`, name(k)]} />
              {target != null ? <ReferenceLine y={target} stroke="hsl(160 60% 35%)" strokeDasharray="5 4" /> : null}
              {games.map((g, i) => <Line key={g} type="monotone" dataKey={g} name={g} stroke={LINES[i % LINES.length]} strokeWidth={2} dot={{ r: 3.5 }} connectNulls />)}
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </div>
  );
}

function Empty({ text }) {
  return <div className="h-full grid place-items-center text-center text-sm text-slate-400 px-4">{text}</div>;
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className="font-display text-xl font-semibold text-slate-900 tabular-nums">{value}{sub ? <span className="ml-1 text-xs font-normal text-slate-400">{sub}</span> : null}</div>
    </div>
  );
}
